import MiniSearch, { type Query, type SearchOptions, type SearchResult } from 'minisearch';

import type { GuideFaqEntry, GuideGlossaryEntry, GuideTopic, GuideTopicItem } from './types';
import { stopwords } from './stopwords';
import { guideTopicPath, type VisibleGuide } from './useGuide';

/** Case- and accent-insensitive form, as in the command palette. */
export function normalizeText(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/**
 * The normalized words of a search query. With a locale, without its stop
 * words (#2108): a query of stop words only is empty, so it opens no panel.
 */
export function searchWords(query: string, locale?: string): string[] {
  if (locale === undefined) return normalizeText(query).split(/\s+/).filter(Boolean);
  const stop = stopwords(locale, normalizeText);
  return normalizeText(query)
    .split(SEPARATOR)
    .filter((word) => word && !stop.has(word));
}

const ENTITIES: Record<string, string> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ' };

/** The text of an HTML fragment, tags dropped and the entities the guide build writes decoded. */
export function htmlText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:amp|lt|gt|quot|#39|nbsp);/g, (entity) => ENTITIES[entity])
    .replace(/\s+/g, ' ')
    .trim();
}

// MiniSearch's own default; the highlighter splits text the same way.
const SEPARATOR = /[\n\r\p{Z}\p{P}]+/u;

const HYPHEN = /[-\u2010\u2011]/u;
const HYPHEN_ALL = /[-\u2010\u2011]/gu;
// A word between separators, its inner hyphens kept: "Eltern-App".
const WORD = /[^\n\r\p{Z}\p{P}]+(?:[-\u2010\u2011][^\n\r\p{Z}\p{P}]+)*/gu;

function wordsOf(text: string): string[] {
  return text.match(WORD) ?? [];
}

/** A page ranks above its tasks, questions and terms on equal evidence (#2108); its callouts do not. */
const PAGE_BOOST = 1.5;
/**
 * A hit whose title, or for a page one of its keywords, holds every query word
 * ranks higher ("Getting started with the parent app" for "parent app"), and
 * higher again when it says nothing else ("Add a child", not "Add a contact to
 * a child", for "how do I add a child"). A synonym counts when all its words
 * are there (#2108).
 */
const TITLE_BOOST = 2;
/**
 * The other entries of a synonym group share half the weight of the word the
 * user typed, so a hit that matches many of them does not outrank one that
 * matches the typed word (#2108).
 */
const SYNONYM_WEIGHT = 0.5;

/** What a hit is; callouts and the page itself are both `page` hits. */
export type GuideHitKind = 'page' | 'task' | 'question' | 'term';

const KIND_ORDER: Record<GuideHitKind, number> = { page: 0, task: 1, question: 2, term: 3 };

interface GuideDocument {
  id: string;
  kind: GuideHitKind;
  topicId: string;
  /** The anchor the hit opens at; none opens the topic at its top. */
  slug?: string;
  title: string;
  keywords: string;
  text: string;
  area: string;
  /** For a callout: the heading of its section, shown as the hit's second line. */
  heading?: string;
  /** Plain text for the snippet. */
  snippet: string;
  item?: GuideTopicItem;
  question?: GuideFaqEntry;
  term?: GuideGlossaryEntry;
}

export interface GuideHit {
  id: string;
  kind: GuideHitKind;
  topic: GuideTopic;
  /** The topic path, at the hit's anchor. */
  path: string;
  title: string;
  heading?: string;
  /** Plain text the snippet is cut from. */
  text: string;
  score: number;
  /** The words of the guide that matched, normalized; for highlighting. */
  terms: string[];
  item?: GuideTopicItem;
  question?: GuideFaqEntry;
  term?: GuideGlossaryEntry;
}

export interface GuideSearchResults {
  /** One hit per topic: the page itself or its best callout. */
  pages: GuideHit[];
  tasks: GuideHit[];
  questions: GuideHit[];
  terms: GuideHit[];
  /** Every hit by score, pages before tasks before questions before terms at equal score. */
  ranked: GuideHit[];
  total: number;
  /** No hit had every word; these hits have some of them. */
  partial: boolean;
  /** Every matched word of the guide, normalized; for highlighting. */
  matched: string[];
}

export interface GuideIndex {
  search(query: string, kinds?: readonly GuideHitKind[]): GuideSearchResults;
}

const SEARCH_OPTIONS: SearchOptions = {
  prefix: true,
  // One edit per five letters; none on short words, they would match half the guide.
  fuzzy: (term) => (term.length < 4 ? false : 0.2),
  boost: { title: 4, keywords: 3, text: 1, area: 1 },
  combineWith: 'AND',
};

/**
 * The query clauses of `query` (#2108), AND-ed by the caller: one per word,
 * stop words dropped; a hyphenated word is its joined form or all its parts;
 * a word or phrase of a synonym group is any entry of the group, the longest
 * entry that matches winning.
 */
function queryClauses(query: string, stop: ReadonlySet<string>, synonyms: readonly (readonly string[])[]): QueryClause[] {
  const wordClause = (word: string): Query | undefined => {
    const parts = word.split(HYPHEN).filter(Boolean);
    const content = parts.filter((part) => !stop.has(part));
    if (content.length === 0) return undefined;
    if (parts.length === 1) return word;
    return { combineWith: 'OR', queries: [parts.join(''), content.length === 1 ? content[0] : { combineWith: 'AND', queries: content }] };
  };
  const phraseClause = (words: readonly string[]): Query | undefined => {
    const clauses = words.flatMap((word) => wordClause(word) ?? []);
    return clauses.length <= 1 ? clauses[0] : { combineWith: 'AND', queries: clauses };
  };
  const groupClause = (group: readonly string[][], typed: readonly string[]): Query | undefined => {
    const own = phraseClause(typed);
    const same = (entry: readonly string[]) => entry.length === typed.length && entry.every((word, i) => word === typed[i]);
    const others = group.filter((entry) => !same(entry)).flatMap((entry) => phraseClause(entry) ?? []);
    const alternatives: Query | undefined =
      others.length === 0 ? undefined : { combineWith: 'OR', boostTerm: () => SYNONYM_WEIGHT / others.length, queries: others };
    const parts = [own, alternatives].filter((part): part is Query => part !== undefined);
    return parts.length <= 1 ? parts[0] : { combineWith: 'OR', queries: parts };
  };
  const groups = synonyms.map((group) => group.map((entry) => wordsOf(normalizeText(entry))).filter((words) => words.length > 0));
  const words = wordsOf(normalizeText(query));
  const clauses: QueryClause[] = [];
  for (let at = 0; at < words.length; ) {
    let best: { group: string[][]; length: number } | undefined;
    for (const group of groups) {
      for (const entry of group) {
        if (entry.length > (best?.length ?? 0) && entry.every((word, i) => words[at + i] === word)) best = { group, length: entry.length };
      }
    }
    const typed = words.slice(at, at + (best?.length ?? 1));
    const clause = best ? groupClause(best.group, typed) : wordClause(words[at]);
    const own = phraseClause(typed);
    if (clause) clauses.push({ query: clause, typed: own ? alternativesOf(own) : [] });
    at += typed.length;
  }
  return clauses;
}

interface QueryClause {
  query: Query;
  /** The ways the words the user typed can match, synonyms left out. */
  typed: string[][];
}

/** The ways a query tree can match, each a list of words that must all be found. */
function alternativesOf(query: Query): string[][] {
  if (typeof query === 'string') return [[query]];
  if (typeof query !== 'object') return [];
  const children = query.queries.map(alternativesOf);
  if (query.combineWith === 'AND') return children.reduce<string[][]>((all, next) => all.flatMap((way) => next.map((more) => [...way, ...more])), [[]]);
  return children.flat();
}

/** Does a title word, joined or one of its parts, start with the query word `leaf`? */
function titleWordStarts(word: string, leaf: string, stop: ReadonlySet<string>): boolean {
  return [word.replace(HYPHEN_ALL, ''), ...word.split(HYPHEN)].some((part) => !stop.has(part) && part.startsWith(leaf));
}

const EMPTY: GuideSearchResults = { pages: [], tasks: [], questions: [], terms: [], ranked: [], total: 0, partial: false, matched: [] };

function sectionText(html: string, items: readonly GuideTopicItem[]): string {
  return [htmlText(html), ...items.flatMap((item) => [item.title, htmlText(item.html)])].filter(Boolean).join(' ');
}

function documentsOf(guide: VisibleGuide): GuideDocument[] {
  const areaTitle = new Map(guide.allSections.map((section) => [section.key, section.title]));
  const documents: GuideDocument[] = [];
  for (const topic of guide.topics) {
    const what = topic.sections.find((section) => section.key === 'what');
    const whatText = what ? sectionText(what.html, what.items) : '';
    documents.push({
      id: `page:${topic.id}`,
      kind: 'page',
      topicId: topic.id,
      title: topic.title,
      keywords: topic.keywords.join(' '),
      text: [topic.summary, whatText].filter(Boolean).join(' '),
      area: areaTitle.get(topic.area) ?? '',
      snippet: topic.summary || whatText,
    });
    for (const section of topic.sections) {
      if (section.key === 'where' || section.key === 'next') {
        const text = sectionText(section.html, section.items);
        documents.push({
          id: `section:${topic.id}#${section.slug}`,
          kind: 'page',
          topicId: topic.id,
          slug: section.slug,
          title: '',
          keywords: '',
          text,
          area: '',
          heading: section.title,
          snippet: text,
        });
      }
      if (section.key === 'howTo') {
        for (const item of section.items) {
          const text = htmlText(item.html);
          documents.push({
            id: `task:${topic.id}#${item.slug}`,
            kind: 'task',
            topicId: topic.id,
            slug: item.slug,
            title: item.title,
            keywords: '',
            text,
            area: '',
            snippet: text,
            item,
          });
        }
      }
    }
  }
  for (const question of guide.faq) {
    const text = htmlText(question.html);
    documents.push({
      id: `question:${question.topicId}#${question.slug}`,
      kind: 'question',
      topicId: question.topicId,
      slug: question.slug,
      title: question.question,
      keywords: '',
      text,
      area: question.areaTitle,
      snippet: text,
      question,
    });
  }
  for (const term of guide.glossary) {
    const text = htmlText(term.html);
    documents.push({
      id: `term:${term.topicId}#${term.slug}`,
      kind: 'term',
      topicId: term.topicId,
      slug: term.slug,
      title: term.term,
      keywords: '',
      text,
      area: term.areaTitle,
      snippet: text,
      term,
    });
  }
  return documents;
}

/**
 * The search index of a visible guide (#2076): pages, callouts, how-to tasks,
 * questions and terms, found by prefix, by one typo per five letters, and by
 * every word (any word when no hit has every word).
 */
export function buildGuideIndex(guide: VisibleGuide): GuideIndex {
  const documents = documentsOf(guide);
  const byId = new Map(documents.map((document) => [document.id, document]));
  const stop = stopwords(guide.locale, normalizeText);
  const miniSearch = new MiniSearch<GuideDocument>({
    fields: ['title', 'keywords', 'text', 'area'],
    // A hyphenated word is indexed as its parts and joined: eltern, app, elternapp.
    tokenize: (text) =>
      wordsOf(text).flatMap((word) => {
        const parts = word.split(HYPHEN).filter(Boolean);
        if (parts.length === 1 || parts.every((part) => stop.has(normalizeText(part)))) return parts;
        return [...parts, parts.join('')];
      }),
    processTerm: (term) => {
      const word = normalizeText(term);
      return word && !stop.has(word) ? word : null;
    },
    searchOptions: SEARCH_OPTIONS,
  });
  // The content words of each hit's title and, for a page, of each of its keywords; callouts have none.
  const labelWords = (label: string) => wordsOf(normalizeText(label)).filter((word) => word.split(HYPHEN).some((part) => !stop.has(part)));
  const labels = new Map(
    documents.map((document) => [
      document.id,
      [
        labelWords(document.title),
        ...(document.kind === 'page' && !document.slug ? (guide.topicById.get(document.topicId)?.keywords ?? []) : [])
          .map(labelWords)
          .filter((words) => words.length > 0),
      ],
    ]),
  );
  // Page boost times title boosts, per hit, for the clauses of one query.
  const boostOf = (clauses: readonly QueryClause[]) => {
    const withSynonyms = clauses.map((clause) => alternativesOf(clause.query));
    const typed = clauses.map((clause) => clause.typed);
    const memo = new Map<string, number>();
    return (id: string) => {
      let boost = memo.get(id);
      if (boost === undefined) {
        const labelBoost = (alternatives: readonly string[][][]) => (words: readonly string[]) => {
          const hit = (leaf: string) => words.filter((word) => titleWordStarts(word, leaf, stop));
          // Every clause has a way to match whose words are all in the label.
          const found = alternatives.map((ways) => ways.filter((way) => way.every((leaf) => hit(leaf).length > 0)));
          if (!found.every((ways) => ways.length > 0)) return 1;
          const covered = new Set(found.flat(2).flatMap(hit));
          return words.every((word) => covered.has(word)) ? TITLE_BOOST * TITLE_BOOST : TITLE_BOOST;
        };
        // The title counts synonyms; a keyword, itself a synonym the author chose, only the typed words.
        const [title = [], ...keywords] = labels.get(id) ?? [];
        boost =
          (id.startsWith('page:') ? PAGE_BOOST : 1) *
          Math.max(1, title.length > 0 ? labelBoost(withSynonyms)(title) : 1, ...keywords.map(labelBoost(typed)));
        memo.set(id, boost);
      }
      return boost;
    };
  };
  miniSearch.addAll(documents);

  const toHit = (result: SearchResult): GuideHit | undefined => {
    const document = byId.get(result.id as string);
    const topic = document ? guide.topicById.get(document.topicId) : undefined;
    if (!document || !topic) return undefined;
    return {
      id: document.id,
      kind: document.kind,
      topic,
      path: guideTopicPath(topic.id, document.slug),
      title: document.title || topic.title,
      heading: document.heading,
      text: document.snippet,
      score: result.score,
      terms: result.terms,
      item: document.item,
      question: document.question,
      term: document.term,
    };
  };

  return {
    search(query, kinds) {
      const queryClauseList = queryClauses(query, stop, guide.synonyms);
      const clauses = queryClauseList.map((clause) => clause.query);
      if (clauses.length === 0) return EMPTY;
      const allowed = kinds ? new Set(kinds) : undefined;
      const options: SearchOptions = {
        boostDocument: boostOf(queryClauseList),
        ...(allowed ? { filter: (result) => allowed.has(byId.get(result.id as string)?.kind as GuideHitKind) } : {}),
      };
      let results = miniSearch.search({ combineWith: 'AND', queries: clauses }, options);
      let partial = false;
      if (results.length === 0 && clauses.length > 1) {
        results = miniSearch.search({ combineWith: 'OR', queries: clauses }, options);
        partial = results.length > 0;
      }
      // MiniSearch multiplies a score by the number of query terms the hit matched, and every
      // synonym and compound part is a term of its own; count the query words (clauses) instead.
      const clauseWords = clauses.map((clause) => new Set(alternativesOf(clause).flat()));
      results = results.map((result) => {
        const matched = clauseWords.filter((words) => result.queryTerms.some((term) => words.has(term))).length;
        return { ...result, score: (result.score / Math.max(1, result.queryTerms.length)) * Math.max(1, matched) };
      });
      const hits = results
        .flatMap((result) => toHit(result) ?? [])
        .sort((a, b) => b.score - a.score || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
      // A topic is one page hit: the best of the page itself and its callouts.
      const seenTopics = new Set<string>();
      const ranked = hits.filter((hit) => {
        if (hit.kind !== 'page') return true;
        if (seenTopics.has(hit.topic.id)) return false;
        seenTopics.add(hit.topic.id);
        return true;
      });
      const ofKind = (kind: GuideHitKind) => ranked.filter((hit) => hit.kind === kind);
      return {
        pages: ofKind('page'),
        tasks: ofKind('task'),
        questions: ofKind('question'),
        terms: ofKind('term'),
        ranked,
        total: ranked.length,
        partial,
        matched: [...new Set(results.flatMap((result) => result.terms))],
      };
    },
  };
}

const INDEXES = new WeakMap<VisibleGuide, GuideIndex>();

/** The index of `guide`, built once per guide object (one per locale and access). */
export function guideIndex(guide: VisibleGuide): GuideIndex {
  let index = INDEXES.get(guide);
  if (!index) {
    index = buildGuideIndex(guide);
    INDEXES.set(guide, index);
  }
  return index;
}

export interface HighlightPart {
  text: string;
  match: boolean;
}

/** `text` cut into runs, the words whose normalized form is in `terms` marked. */
export function highlightParts(text: string, terms: readonly string[]): HighlightPart[] {
  if (terms.length === 0) return [{ text, match: false }];
  const wanted = new Set(terms);
  const parts: HighlightPart[] = [];
  const push = (value: string, match: boolean) => {
    if (!value) return;
    const last = parts.at(-1);
    if (last && last.match === match) last.text += value;
    else parts.push({ text: value, match });
  };
  const separator = new RegExp(SEPARATOR.source, 'gu');
  let at = 0;
  for (const found of text.matchAll(separator)) {
    const word = text.slice(at, found.index);
    push(word, wanted.has(normalizeText(word)));
    push(found[0], false);
    at = found.index + found[0].length;
  }
  const rest = text.slice(at);
  push(rest, wanted.has(normalizeText(rest)));
  return parts;
}

/** About `length` characters of `text` around the first matched word, with ellipses where cut. */
export function searchSnippet(text: string, terms: readonly string[], length = 140): string {
  if (text.length <= length) return text;
  const normalized = normalizeText(text);
  const first = terms.reduce((best, term) => {
    const found = normalized.search(new RegExp(`(^|[^\\p{L}\\p{N}])${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'u'));
    return found >= 0 && found < best ? found : best;
  }, Number.POSITIVE_INFINITY);
  if (!Number.isFinite(first) || first < length / 2) return `${text.slice(0, length).trimEnd()}…`;
  const start = Math.max(0, Math.min(first - Math.floor(length / 4), text.length - length));
  const cut = text.slice(start, start + length).trim();
  return `…${cut}${start + length < text.length ? '…' : ''}`;
}
